'use client';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/shadcn/ui/form';
import { PhoneInput } from '@/shadcn/ui/phone-input';
import JoinWaitlistButton from '@/components/global/ui/JoinWaitlistButton';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { useWaitlistForm } from '@/hooks/useWaitlistForm';

interface WaitlistFormProps {
  className?: string;
  onSuccess?: () => void;
}

export default function WaitlistForm({
  className,
  onSuccess,
}: WaitlistFormProps) {
  const { form, loading, handleFormStarted, handleEmailBlur, onSubmit } =
    useWaitlistForm({
      formName: 'waitlist',
      onSuccess,
    });

  return (
    <div className={cn('w-full', className)}>
      <Form {...form}>
        <form
          className="w-full flex flex-col justify-start items-stretch gap-[2rem]"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          {/* Name Field */}
          <FormField
            control={form.control}
            name="name"
            disabled={loading}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <input
                    type="text"
                    placeholder="Your name"
                    {...field}
                    onFocus={() => handleFormStarted()}
                    className="w-full rounded-[0.5rem] border-[.125rem] border-[rgba(255,255,255,0.16)] bg-black h-[5.375rem] placeholder:text-offwhite placeholder:opacity-70 text-[2rem] text-offwhite font-inter-tight focus:outline-none focus:ring-1 focus:ring-offset-2 focus:ring-offset-black focus:ring-white px-[2rem]"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Email Field */}
          <FormField
            control={form.control}
            name="email"
            disabled={loading}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <input
                    type="email"
                    placeholder="Your best email"
                    {...field}
                    onFocus={() => handleFormStarted()}
                    onBlur={(e) => {
                      field.onBlur();
                      handleEmailBlur();
                    }}
                    className="w-full rounded-[0.5rem] border-[.125rem] border-[rgba(255,255,255,0.16)] bg-black h-[5.375rem] placeholder:text-offwhite placeholder:opacity-70 text-[2rem] text-offwhite font-inter-tight focus:outline-none focus:ring-1 focus:ring-offset-2 focus:ring-offset-black focus:ring-white px-[2rem]"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Phone Field */}
          <FormField
            control={form.control}
            name="phone"
            disabled={loading}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <PhoneInput
                    placeholder="Your phone number"
                    defaultCountry="DE"
                    value={String(field.value || '')}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    disabled={loading}
                    className="w-full rounded-[0.5rem] border-[.125rem] border-[rgba(255,255,255,0.16)] bg-black h-[5.375rem] placeholder:text-offwhite placeholder:opacity-70 text-[2rem] text-offwhite font-inter-tight focus:outline-none focus:ring-1 focus:ring-offset-2 focus:ring-offset-black focus:ring-white"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Terms Notice */}
          <div className="text-[1.5rem]  leading-[120%] text-offwhite/70 font-inter-tight font-[300]">
            By clicking on 'Join the release waitlist' you agree to receive
            information and accept our{' '}
            <Link
              href="/terms-conditions"
              className="underline hover:opacity-70 transition-opacity"
            >
              Terms & Conditions
            </Link>{' '}
            and{' '}
            <Link
              href="/privacy-policy"
              className="underline hover:opacity-70 transition-opacity"
            >
              Privacy Policy
            </Link>
            .
          </div>

          {/* Submit Button */}
          <JoinWaitlistButton
            className="lg:mt-0 mt-[3rem]"
            type="submit"
            disabled={loading}
          >
            {loading ? 'Joining...' : 'Join the release waitlist'}
          </JoinWaitlistButton>
        </form>
      </Form>
    </div>
  );
}
